import { useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Spinner } from 'aimo-ui';
import AccountContext from 'context/AccountContext';
import LongTermParkingContext from 'context/LongTermParkingContext';
import useAfterAuthenticationRedirect from 'hooks/useAfterAuthenticationRedirect';
import useHasVehicleAndCreditCard from 'hooks/useHasVehicleAndCreditCard';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import React, { useContext } from 'react';
import { ScrollView } from 'react-native';
import { onboardingCompleted } from 'states/persistInStorage';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { usePermitStore } from 'stores/permitStore';
import { ColumnWithMargins, StyledWrapper } from 'styles/ContainerStyles';
import { ResolvedPermit } from 'types/common';
import {
  ParkZoneBenefit,
  Pmc,
  ReservedParkingSpace,
} from 'types/generatedSchemaTypes';
import { LongTermStackParamList } from 'types/navigation';
import { getPriceForSalesOrder } from 'utils/commonUtils';
import {
  compilePmcDetails,
  handlePermitConditions,
  isPmcAvailable,
  isPmcVisible,
  resolvePermitDetails,
  resolvePmcMemberGroupLock,
} from 'utils/permitUtils';
import NonReservedPermits from './NonReservedPermits';
import ReservedPermits from './ReservedPermits';

const ParkingZonePermits = () => {
  const { isLoggedIn } = useIsLoggedIn();
  const { loading, error, selectedPmcs, parkingZoneResult } = useContext(
    LongTermParkingContext
  );
  const {
    setPmcIsLockedByMemberGroup,
    resetPmcIsLockedByMemberGroup,
    setSelectedPermit,
    setSelectedParkZoneBenefit,
  } = usePermitStore();
  const isOnboardingCompleted = useReactiveVar(onboardingCompleted);
  const hasVehicleAndCard = useHasVehicleAndCreditCard();
  const { navigate } =
    useNavigation<StackNavigationProp<LongTermStackParamList>>();
  const {
    customer: { householdBenefits, permits },
    benefitsLoading: benefitsQueryLoading,
  } = useContext(AccountContext);

  const parkingZoneBenefits = (
    parkingZoneResult?.data?.parkZone?.benefits ?? []
  ).filter((b: any): b is ParkZoneBenefit => !!b);
  const benefitsLoading = parkingZoneResult?.loading || benefitsQueryLoading;

  const pmcStatusDetail = compilePmcDetails(permits ?? []);
  const {
    setReturnRedirectToBuyPermit,
    navigateToAccountLogin,
    navigateToOnboarding,
  } = useAfterAuthenticationRedirect();

  const handleNavigate = (
    permit: ResolvedPermit,
    pmcIsLockedForTheCustomer?: boolean,
    memberGroupBenefit?: ParkZoneBenefit | undefined,
    pmcIsInsideALockedMemberGroup?: boolean
  ) => {
    const {
      isWaitingActivation,
      exceedMaximumBenefit,
      isDuplicatePermit,
      orderLineIdWithVersion,
      ...pmcProperties
    } = permit;

    const pmc: Pmc = {
      ...pmcProperties,
    };

    const pmcId = pmc?.pmcId ?? '';

    if (!isLoggedIn) {
      setReturnRedirectToBuyPermit(pmcId);
      navigateToAccountLogin();
      return;
    }

    if (!isOnboardingCompleted) {
      // TODO make the parameter to navigate surely to onboarding -> simplify
      if (hasVehicleAndCard) {
        onboardingCompleted(true);
      } else {
        setReturnRedirectToBuyPermit(pmcId, undefined, false);
        navigateToOnboarding();
        return;
      }
    }

    setSelectedPermit(orderLineIdWithVersion);
    useParkzoneStore.getState().setSelectedParkzonePmcUnion(permit);
    setSelectedParkZoneBenefit(memberGroupBenefit);
    resetPmcIsLockedByMemberGroup();
    if (pmcId) {
      setPmcIsLockedByMemberGroup(
        pmcId,
        !!pmcIsLockedForTheCustomer,
        !!pmcIsInsideALockedMemberGroup
      );
    }
    if (
      handlePermitConditions(
        permit,
        pmc,
        'ParkingZonePermit',
        !!pmcIsLockedForTheCustomer,
        !!pmcIsInsideALockedMemberGroup
      )
    ) {
      return;
    }

    if (isPmcAvailable(pmc).showWaitingList) {
      return navigate('waitingListInfo', {
        parkingZoneUid: undefined,
        pmcId: pmc.pmcId ?? '',
      });
    }

    navigate('buyScreen', { pmcId });
  };

  if (loading) {
    return (
      <ColumnWithMargins mt={30}>
        <StyledWrapper center mb={20}>
          <Spinner />
        </StyledWrapper>
      </ColumnWithMargins>
    );
  }

  if (error || !selectedPmcs) {
    return <></>;
  }

  const permitOccurrenceMap = selectedPmcs.map((permit) => ({
    ...permit,
    ...resolvePermitDetails(
      permit,
      householdBenefits ?? undefined,
      pmcStatusDetail
    ),
  }));

  return (
    <ScrollView>
      <ColumnWithMargins mt={30}>
        {permitOccurrenceMap
          ?.filter((permit) => isPmcVisible(permit, householdBenefits ?? []))
          .map((permit, index: number) => {
            const {
              pmcId = '',
              name = '',
              prices,
              reservationType,
              parkingSpacesV2,
              countryCode: permitCountry,
              visibility,
            } = permit;
            const thirtyDayPrice = getPriceForSalesOrder(prices, permitCountry);
            const memberGroupName = householdBenefits?.find(
              (benefit) => benefit.benefitTargetUid === pmcId
            )?.name;
            const {
              memberGroupBenefit,
              pmcIsInsideALockedMemberGroup,
              pmcIsLockedForTheCustomer,
            } = resolvePmcMemberGroupLock(
              pmcId,
              parkingZoneBenefits,
              householdBenefits,
              visibility
            );

            if (reservationType === 'RESERVED') {
              return (
                <ReservedPermits
                  parkingSpaces={
                    (parkingSpacesV2?.content ?? []) as ReservedParkingSpace[]
                  }
                  priceLabel={thirtyDayPrice.totalPrice}
                  pmcId={pmcId as string}
                  name={name as string}
                  testID={`reserved-permits-${index}`}
                  key={`${pmcId}-${index}`}
                  handleNavigate={() =>
                    handleNavigate(
                      permit as ResolvedPermit,
                      pmcIsLockedForTheCustomer,
                      memberGroupBenefit,
                      pmcIsInsideALockedMemberGroup
                    )
                  }
                  memberGroupName={memberGroupName}
                  pmc={permit}
                  pmcIsLockedForTheCustomer={pmcIsLockedForTheCustomer}
                  pmcIsInsideALockedMemberGroup={pmcIsInsideALockedMemberGroup}
                  disabled={benefitsLoading}
                />
              );
            }
            return (
              <NonReservedPermits
                key={`${pmcId}-${index}`}
                pmc={permit as Pmc}
                testID={`non-reserved-permits-${index}`}
                priceLabel={thirtyDayPrice.totalPrice}
                handleNavigate={() => {
                  handleNavigate(
                    permit as ResolvedPermit,
                    pmcIsLockedForTheCustomer,
                    memberGroupBenefit,
                    pmcIsInsideALockedMemberGroup
                  );
                }}
                memberGroupName={memberGroupName}
                pmcIsLockedForTheCustomer={pmcIsLockedForTheCustomer}
                pmcIsInsideALockedMemberGroup={pmcIsInsideALockedMemberGroup}
                disabled={benefitsLoading}
              />
            );
          })}
      </ColumnWithMargins>
    </ScrollView>
  );
};

export default ParkingZonePermits;
